import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { Invoice, businessAnalyticsApi } from '../../api';
import { BusinessAnalytics } from '../../api_types';
import LoadingSpinner from '../../shared/components/LoadingSpinner';

export default function SubscriptionAnalytics({ businessId, facilityId }: { businessId: string, facilityId: string }) {
  // Debug logs
  const [analytics, setAnalytics] = useState<BusinessAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasSubscriptionErrors, setHasSubscriptionErrors] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [invoiceHistory, setInvoiceHistory] = useState<Record<string, { loading: boolean; data: Invoice[] | null; error: string | null }>>({});
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);
        setHasSubscriptionErrors(false);

        if (!businessId || !facilityId) {
          throw new Error('No businessId or facilityId');
        }

        if (!currentUser) {
          throw new Error('No authenticated user');
        }

        const token = await currentUser.getIdToken();
        console.log('Token:', token); // Remove this in production
        const data = await businessAnalyticsApi.getSubscriptionAnalytics(businessId, facilityId, token);
        
        // Check if any subscribers have error states
        const hasErrors = data.subscribers.some(sub => 
          sub.subscriptionStatus === 'error' || 
          (sub.subscriptionStatus === 'active' && !sub.subscriptionAmount)
        );
        setHasSubscriptionErrors(hasErrors);
        setAnalytics(data);
      } catch (err) {
        console.error('Analytics fetch error:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    if (facilityId) {
      fetchAnalytics();
    }
  }, [businessId, facilityId, currentUser]);

  // Debug logs for render state
  console.log('SubscriptionAnalytics state', { loading, error, analytics });

  const toggleRow = async (subscriberId: string) => {
    if (!currentUser) return;
    
    try {
      const token = await currentUser.getIdToken();
      const invoices = await businessAnalyticsApi.getMemberInvoices(subscriberId, token, businessId, facilityId);
      
      // Toggle the expanded state
      const newExpandedRows = new Set(expandedRows);
      if (newExpandedRows.has(subscriberId)) {
        newExpandedRows.delete(subscriberId);
      } else {
        newExpandedRows.add(subscriberId);
      }
      setExpandedRows(newExpandedRows);
      
      // Update invoice history
      setInvoiceHistory(prev => ({
        ...prev,
        [subscriberId]: { loading: false, data: invoices, error: null }
      }));
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setError('Failed to fetch invoices');
    }
  };

  if (loading) {
    console.log('Rendering: LoadingSpinner');
    return <LoadingSpinner />;
  }

  if (error) {
    console.log('Rendering: Error', error);
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  if (!analytics) {
    console.log('Rendering: No analytics data');
    return <div className="text-gray-500 p-4">No analytics data available.</div>;
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'canceled':
        return 'bg-red-100 text-red-800';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Calculate total initiation fees
  const totalInitiationFees = analytics.subscribers.reduce((sum, sub) => 
    sum + (sub.initiationFee || 0), 0
  );

  return (
    <div className="space-y-6">
      {hasSubscriptionErrors && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-700">
          <p className="flex items-center">
            <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Some subscription data could not be loaded. The analytics shown may be incomplete.
          </p>
        </div>
      )}

      {/* Metrics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">Monthly Recurring Revenue</h3>
          <p className="mt-2 text-3xl font-semibold text-gray-900">
            {formatCurrency(analytics.summary.monthlyRecurringRevenue)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">Active Subscribers</h3>
          <p className="mt-2 text-3xl font-semibold text-gray-900">
            {analytics.summary.activeSubscribers}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">Average Revenue Per User</h3>
          <p className="mt-2 text-3xl font-semibold text-gray-900">
            {formatCurrency(analytics.summary.averageRevenuePerUser)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">Revenue (Last 30 Days)</h3>
          <p className="mt-2 text-3xl font-semibold text-gray-900">
            {formatCurrency(analytics.summary.revenue30Days)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">Total Initiation Fees</h3>
          <p className="mt-2 text-3xl font-semibold text-gray-900">
            {formatCurrency(totalInitiationFees)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">Total Subscribers</h3>
          <p className="mt-2 text-3xl font-semibold text-gray-900">
            {analytics.summary.totalSubscribers}
          </p>
        </div>
      </div>

      {/* Subscribers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Subscribers</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subscription
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Initiation Fee
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Next Billing
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Payment
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analytics.subscribers.map((subscriber) => (
                <React.Fragment key={subscriber.id}>
                  <tr 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleRow(subscriber.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <svg 
                            className={`h-5 w-5 transform transition-transform ${expandedRows.has(subscriber.id) ? 'rotate-90' : ''}`}
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                        <div className="ml-4 flex flex-col">
                          <div className="text-sm font-medium text-gray-900">{subscriber.name}</div>
                          <div className="text-sm text-gray-500">{subscriber.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(subscriber.subscriptionStatus)}`}>
                        {subscriber.subscriptionStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCurrency(subscriber.subscriptionAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {subscriber.initiationFee ? formatCurrency(subscriber.initiationFee) : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(subscriber.nextBillingDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(subscriber.startDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(subscriber.lastPaymentDate)}
                    </td>
                  </tr>
                  {expandedRows.has(subscriber.id) && (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 bg-gray-50">
                        <div className="space-y-4">
                          <h4 className="text-sm font-medium text-gray-900">Invoice History</h4>
                          {invoiceHistory[subscriber.id]?.loading ? (
                            <div className="flex justify-center py-4">
                              <LoadingSpinner />
                            </div>
                          ) : invoiceHistory[subscriber.id]?.error ? (
                            <div className="text-sm text-red-600">
                              {invoiceHistory[subscriber.id].error}
                            </div>
                          ) : invoiceHistory[subscriber.id]?.data ? (
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {invoiceHistory[subscriber.id]?.data?.map((invoice) => (
                                    <tr key={invoice.id}>
                                      <td className="px-4 py-2 text-sm text-gray-900">
                                        {formatDate(invoice.date)}
                                      </td>
                                      <td className="px-4 py-2 text-sm text-gray-900">
                                        {formatCurrency(invoice.amount)}
                                      </td>
                                      <td className="px-4 py-2 text-sm">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                                          {invoice.status}
                                        </span>
                                      </td>
                                      <td className="px-4 py-2 text-sm text-gray-900">
                                        <div className="flex space-x-2">
                                          {invoice.pdfUrl && (
                                            <a
                                              href={invoice.pdfUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-indigo-600 hover:text-indigo-900"
                                            >
                                              PDF
                                            </a>
                                          )}
                                          {invoice.hostedUrl && (
                                            <a
                                              href={invoice.hostedUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-indigo-600 hover:text-indigo-900"
                                            >
                                              View
                                            </a>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
} 